/** AlgoliaGuard: reject / timeout / empty-index → Craft fallback (browser only). */

export const TIMEOUT_MS = 10000

const SIM_STALL_MS = TIMEOUT_MS + 2000

export const craftSearchUrl = (query) => {
    const params = new URLSearchParams()
    const q = typeof query === 'string' ? query.trim() : ''
    if (q) params.set('q', q)
    params.set('provider', 'craft')
    return `/search?${params.toString()}`
}

export const isFailSimAllowed = () => globalThis.document?.body?.dataset?.searchAllowFailSim === '1'

/** @returns {'reject'|'timeout'|'empty'|null} */
export const getFailSimMode = () => {
    if (!isFailSimAllowed()) return null
    const search = globalThis.window?.location?.search
    if (typeof search !== 'string') return null
    const mode = new URLSearchParams(search).get('searchFail')
    if (mode === 'reject' || mode === 'timeout' || mode === 'empty') return mode
    return null
}

const queryFromRequests = (requests) => {
    const request = requests?.[0] || {}
    const params = typeof request.params === 'string'
        ? Object.fromEntries(new URLSearchParams(request.params))
        : (request.params || {})
    return String(params.query || request.query || '').trim()
}

const isFailedResult = (result) => {
    if (!result || typeof result !== 'object') return true
    if (result.status && result.status >= 400) return true
    if (result.message && !Array.isArray(result.hits)) return true
    return false
}

const makeTimeoutError = () => {
    const error = new Error('AlgoliaGuard timeout')
    error.isGuardTimeout = true
    return error
}

const makeStaleError = () => {
    const error = new Error('AlgoliaGuard: stale')
    error.isGuardStale = true
    return error
}

/**
 * @param {object|null} baseClient
 * @param {{ onFailure?: (info: { reason: string, query: string, generation: number }) => void }} [hooks]
 */
export const createGuardedSearchClient = (baseClient, hooks = {}) => {
    if (!baseClient || typeof baseClient.search !== 'function') return null

    const { onFailure } = hooks
    let generation = 0
    let latchedGeneration = null

    const triggerFailure = (gen, reason, query) => {
        if (gen !== generation) return
        if (latchedGeneration === gen) return
        latchedGeneration = gen
        if (typeof onFailure !== 'function') return
        try {
            onFailure({ reason, query, generation: gen })
        } catch {
            // ignore onFailure errors
        }
    }

    return {
        ...baseClient,
        search(requests) {
            const gen = ++generation

            const failMode = getFailSimMode()
            let networkPromise
            let simStallId = 0

            if (failMode === 'reject') {
                networkPromise = Promise.reject(new Error('searchFail=reject'))
            } else if (failMode === 'timeout') {
                networkPromise = new Promise((_, reject) => {
                    simStallId = globalThis.setTimeout(
                        () => reject(new Error('searchFail=timeout')),
                        SIM_STALL_MS,
                    )
                })
            } else {
                networkPromise = Promise.resolve().then(() => baseClient.search(requests))
            }

            networkPromise.catch(() => {})

            let timeoutId = 0
            const timeoutPromise = new Promise((_, reject) => {
                timeoutId = globalThis.setTimeout(() => reject(makeTimeoutError()), TIMEOUT_MS)
            })

            return Promise.race([networkPromise, timeoutPromise])
                .finally(() => {
                    globalThis.clearTimeout(timeoutId)
                    globalThis.clearTimeout(simStallId)
                })
                .then((response) => {
                    if (gen !== generation) {
                        return Promise.reject(makeStaleError())
                    }

                    if (
                        !response
                        || !Array.isArray(response.results)
                        || response.results.length !== requests.length
                    ) {
                        triggerFailure(gen, 'reject', queryFromRequests(requests))
                        return Promise.reject(new Error('AlgoliaGuard: invalid search response'))
                    }

                    if (response.results.some(isFailedResult)) {
                        triggerFailure(gen, 'reject', queryFromRequests(requests))
                        return Promise.reject(new Error('AlgoliaGuard: rejected result'))
                    }

                    return response
                })
                .catch((error) => {
                    if (error?.isGuardStale) {
                        return Promise.reject(error)
                    }
                    if (gen === generation) {
                        const reason = error?.isGuardTimeout ? 'timeout' : 'reject'
                        triggerFailure(gen, reason, queryFromRequests(requests))
                    }
                    return Promise.reject(error)
                })
        },
    }
}

/**
 * @param {object} client
 * @param {string} indexName
 * @returns {Promise<{ nbHits: number, empty: boolean }>}
 */
export const probeEmptyIndex = async (client, indexName) => {
    if (getFailSimMode() === 'empty') {
        return { nbHits: 0, empty: true }
    }

    const response = await client.search([{
        indexName,
        params: {
            query: '',
            hitsPerPage: 1,
            analytics: false,
        },
    }])

    const rawNbHits = response?.results?.[0]?.nbHits
    if (
        typeof rawNbHits !== 'number'
        || !Number.isFinite(rawNbHits)
        || rawNbHits < 0
    ) {
        throw new Error('AlgoliaGuard: invalid empty-index probe response')
    }

    return {
        nbHits: rawNbHits,
        empty: rawNbHits === 0,
    }
}
